// src/components/journal/Calendar/Calendar.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Info, X, Minus } from 'lucide-react';
import { useTrade } from '../../../context/TradeContext';
import { DayCell } from './DayCell';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/Card';
import MobileAtmosphericLighting from '../../ui/MobileAtmosphericLighting';
import { WeeklyStats } from './WeeklyStats';
import { formatLocalDateToString } from '../../../utils/dateUtils';
import './calendar-styles.css'; // Import external styles

export const Calendar: React.FC = () => {
  // All existing component logic stays the same
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState<'plan' | 'pnl'>('plan');
  
  // Keyboard navigation for tabs
  const handleTabKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setActiveTab('plan');
      document.getElementById('calendar-tab-plan')?.focus();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setActiveTab('pnl');
      document.getElementById('calendar-tab-pnl')?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveTab('plan');
      document.getElementById('calendar-tab-plan')?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveTab('pnl');
      document.getElementById('calendar-tab-pnl')?.focus();
    }
  };
  const [isMobile, setIsMobile] = useState(false);
  
  // NEW: State for X share prompt
  const [isSharePromptExpanded, setIsSharePromptExpanded] = useState(true);
  
  const { trades } = useTrade();
  
  // Fix 2: Guard against SSR/Prerender environments before touching window
  useEffect(() => {
    const checkIfMobile = () => {
      // Check if window exists before accessing it
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    };
    
    checkIfMobile();
    
    // Only add event listener if window exists
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkIfMobile);
      
      return () => {
        window.removeEventListener('resize', checkIfMobile);
      };
    }
  }, []);

  // NEW: Load share prompt preference from localStorage
  useEffect(() => {
    const savedPreference = localStorage.getItem('sharePromptExpanded');
    if (savedPreference !== null) {
      setIsSharePromptExpanded(savedPreference === 'true');
    }
  }, []);

  // NEW: Toggle share prompt and save preference
  const toggleSharePrompt = () => {
    const newState = !isSharePromptExpanded;
    setIsSharePromptExpanded(newState);
    localStorage.setItem('sharePromptExpanded', newState.toString());
  };
  
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  
  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  const year = currentDate.getFullYear();
  
  // Get first day of the month
  const firstDayOfMonth = new Date(year, currentDate.getMonth(), 1).getDay();
  
  // Get number of days in the month
  const daysInMonth = new Date(year, currentDate.getMonth() + 1, 0).getDate();
  
  // LRU cache for date validation with size limit to prevent memory leaks
  const dateValidationCache = useMemo(() => {
    const cache = new Map<string, boolean>();
    const MAX_CACHE_SIZE = 1000;
    return {
      get: (key: string) => {
        const value = cache.get(key);
        if (value !== undefined) {
          // Move to end (most recently used)
          cache.delete(key);
          cache.set(key, value);
        }
        return value;
      },
      set: (key: string, value: boolean) => {
        if (cache.size >= MAX_CACHE_SIZE) {
          const firstKey = cache.keys().next().value;
          if (firstKey) {
            cache.delete(firstKey);
          }
        }
        cache.set(key, value);
      },
      has: (key: string) => cache.has(key)
    };
  }, []);

  const isValidDate = useCallback((dateKey: string): boolean => {
    if (dateValidationCache.has(dateKey)) {
      return dateValidationCache.get(dateKey)!;
    }
    
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
      dateValidationCache.set(dateKey, false);
      return false;
    }
    
    // Use timezone-agnostic validation to avoid UTC conversion issues
    const parts = dateKey.split('-').map(Number);
    const tempDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const isValid = !isNaN(tempDate.getTime()) && 
      tempDate.getFullYear() === parts[0] && 
      tempDate.getMonth() === parts[1] - 1 && 
      tempDate.getDate() === parts[2];
    dateValidationCache.set(dateKey, isValid);
    return isValid;
  }, [dateValidationCache]);

  // Fix 3: Create trades lookup map with standardized date format
  const tradesLookup = useMemo(() => {
    const lookup = new Map<string, typeof trades>();
    trades.forEach(trade => {
      // Since trade.date is already in YYYY-MM-DD format, use it directly as dateKey
      // This avoids timezone conversion issues that occur when converting to Date and back
      const dateKey = trade.date;
      
      // Enhanced date validation with performance caching
      if (!isValidDate(dateKey)) {
        console.warn(`Invalid date for trade: ${dateKey}`);
        return; // Skip this trade
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📅 Calendar: Processing trade date:', trade.date, '→ dateKey:', dateKey);
      }
      
      if (!lookup.has(dateKey)) {
        lookup.set(dateKey, []);
      }
      lookup.get(dateKey)!.push(trade);
    });
    return lookup;
  }, [trades, isValidDate]);
  
  // Optimized function to get trades for a specific date with normalized format
  const getTradesForDate = (date: string) => {
    try {
      // FIXED: Use direct string lookup instead of converting through Date object
      // to prevent timezone shifts
      return tradesLookup.get(date) || [];
    } catch (err) {
      // Handle any date conversion errors
      return [];
    }
  };
  
  // Generate calendar grid
  const calendarDays = [];
  
  // Add empty cells for days before the first of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = `${year}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // Remove debug logging before production
    
    const dayTrades = getTradesForDate(date);
    
    if (dayTrades.length > 0) {
      const pnl = dayTrades.reduce((sum, trade) => sum + trade.pnl, 0);
      const followedPlanCount = dayTrades.filter(trade => trade.followedPlan).length;
      const planAdherenceRate = followedPlanCount / dayTrades.length;
      
      calendarDays.push({
        day,
        date,
        pnl,
        followedPlan: planAdherenceRate >= 0.5,
        planAdherenceRate,
        hasData: true,
        tradeCount: dayTrades.length
      });
    } else {
      calendarDays.push({
        day,
        date,
        pnl: 0,
        followedPlan: false,
        hasData: false,
        tradeCount: 0
      });
    }
  }
  
  // Group calendar days into weeks for weekly stats calculation
  const weeks = [];
  let currentWeek = [];
  
  // Add any empty cells at the start of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    currentWeek.push(null);
  }
  
  // Add the days and create weeks
  for (let i = firstDayOfMonth; i < calendarDays.length; i++) {
    currentWeek.push(calendarDays[i]);
    
    // When we reach the end of a week (Saturday), or the end of the month
    if ((i + 1) % 7 === 0 || i === calendarDays.length - 1) {
      // If it's the last week and we need to pad it
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push([...currentWeek]);
      currentWeek = [];
    }
  }
  
  // Calculate weekly statistics for each week
  const weeklyStats = weeks.map(week => {
    // Filter out null values and get all valid days in the week
    const validDays = week.filter(day => day !== null);
    
    // Get all trades for the week
    const weekTrades = validDays.flatMap(day => 
      day?.hasData ? getTradesForDate(day.date) : []
    );
    
    // Calculate weekly stats
    const totalTrades = weekTrades.length;
    const totalPnl = weekTrades.reduce((sum, trade) => sum + trade.pnl, 0);
    const winningTrades = weekTrades.filter(trade => trade.pnl > 0).length;
    const losingTrades = weekTrades.filter(trade => trade.pnl < 0).length;
    const followedPlanCount = weekTrades.filter(trade => trade.followedPlan).length;
    
    return {
      totalTrades,
      totalPnl,
      winRate: totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0,
      planAdherenceRate: totalTrades > 0 ? (followedPlanCount / totalTrades) * 100 : 0,
      followedPlanCount,
      winningTrades,
      losingTrades
    };
  });

  // Helper function to get the background class for mobile weekly stats
  const getMobileStatsBgClass = (stats: any) => {
    if (activeTab === 'plan') {
      if (stats.planAdherenceRate >= 70) return 'mobile-stats-good';
      if (stats.planAdherenceRate >= 50) return 'mobile-stats-medium';
      return 'mobile-stats-poor';
    } else {
      if (stats.totalPnl > 0) return 'mobile-stats-good';
      if (stats.totalPnl === 0) return 'mobile-stats-medium';
      return 'mobile-stats-poor';
    }
  };
  
  return (
<div className="min-h-screen bg-transparent p-4">  
  <div className="relative">
        <MobileAtmosphericLighting />
        
        <Card className="glass-card shadow-lg border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-[100px] bg-white/30 dark:bg-gray-800/30 w-[98%] md:w-[99%] max-w-[1800px] md:max-w-[2000px] mx-auto">
          <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 px-8 py-8">
            <CardTitle className="text-3xl font-display font-bold text-gray-900 dark:text-gray-100">
              Trading <span className="gradient-text">Calendar</span>
            </CardTitle>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-3 md:space-y-0 md:space-x-4 w-full md:w-auto">
              <div
                role="tablist"
                aria-orientation="horizontal"
                aria-label="Calendar view mode"
                onKeyDown={handleTabKeyDown}
                className="bg-gray-200/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-1 shadow-md"
              >
                <button
                  id="calendar-tab-plan"
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'plan'}
                  aria-controls="calendar-panel-plan"
                  className={`px-4 md:px-5 py-2 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === 'plan' 
                      ? 'bg-white dark:bg-gray-600 border border-primary-200 dark:border-primary-500 shadow-md text-primary-700 dark:text-primary-300 font-semibold' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white/80 dark:hover:bg-gray-600/50 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  onClick={() => setActiveTab('plan')}
                >
                  Plan
                </button>
                <button
                  id="calendar-tab-pnl"
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'pnl'}
                  aria-controls="calendar-panel-pnl"
                  className={`px-4 md:px-5 py-2 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === 'pnl' 
                      ? 'bg-white dark:bg-gray-600 border border-primary-200 dark:border-primary-500 shadow-md text-primary-700 dark:text-primary-300 font-semibold' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white/80 dark:hover:bg-gray-600/50 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                  onClick={() => setActiveTab('pnl')}
                >
                  P&L
                </button>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Fix 1: Add accessible labels to icon-only navigation buttons */}
                <button 
                  type="button"
                  className="p-2 rounded-full hover:bg-white/50 dark:hover:bg-gray-600/50 backdrop-blur-sm transition-all duration-200 shadow-sm hover:shadow-md text-gray-700 dark:text-gray-300"
                  onClick={goToPreviousMonth}
                  aria-label="Previous month"
                >
                  <ChevronLeft size={24} />
                </button>
                <span className="font-medium text-gray-800 dark:text-gray-200 text-lg">
                  {monthName} {year}
                </span>
                <button 
                  type="button"
                  className="p-2 rounded-full hover:bg-white/50 dark:hover:bg-gray-600/50 backdrop-blur-sm transition-all duration-200 shadow-sm hover:shadow-md text-gray-700 dark:text-gray-300"
                  onClick={goToNextMonth}
                  aria-label="Next month"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="px-8 pb-8">
            {/* Plan Tab Panel */}
            <div
              id="calendar-panel-plan"
              role="tabpanel"
              aria-labelledby="calendar-tab-plan"
              hidden={activeTab !== 'plan'}
            >
              {/* Mobile view */}
              {isMobile ? (
                <div className="space-y-6">
                <div className="grid grid-cols-7 gap-1">
                  {/* Day header cells */}
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (
                    <div key={day} className="text-center font-medium py-1 text-gray-700 dark:text-gray-300 text-xs">
                      {day}
                    </div>
                  ))}
                  
                  {/* Render weeks without weekly stats column */}
                  {weeks.map((week, weekIndex) => (
                    <React.Fragment key={`week-${weekIndex}`}>
                      {/* Render days in the week */}
                      {week.map((day, dayIndex) => (
                        <DayCell 
                          key={`day-${weekIndex}-${dayIndex}`} 
                          day={day} 
                          mode="plan"
                          isMobile={true}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </div>
                
                {/* Mobile weekly stats */}
                <div className="space-y-3">
                  {weeklyStats.map((stats, weekIndex) => (
                    stats.totalTrades > 0 ? (
                      <div 
                        key={`mobile-stats-${weekIndex}`} 
                        className={`rounded-xl p-3 shadow-sm border border-gray-200/50 dark:border-gray-600/50 ${getMobileStatsBgClass(stats)}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="font-medium text-sm text-gray-800 dark:text-gray-200">Week {weekIndex + 1}</div>
                          <div className="text-xs text-gray-700 dark:text-gray-300">{stats.totalTrades} trades</div>
                        </div>
                        <div className="flex justify-between mt-2">
                          <div className="flex items-center">
                            <span className="text-sm font-medium mr-2 text-gray-800 dark:text-gray-200">Plan:</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{stats.planAdherenceRate.toFixed(0)}%</span>
                            <span className="text-xs ml-1 text-gray-600 dark:text-gray-400">({stats.followedPlanCount}/{stats.totalTrades})</span>
                          </div>
                        </div>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-3 min-h-[750px]" style={{ gridAutoRows: "1fr" }}>
                {/* Day header cells */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center font-medium py-3 text-gray-700 dark:text-gray-300 text-lg">
                    {day}
                  </div>
                ))}
                
                {/* Weekly stats header */}
                <div className="text-center font-medium py-3 text-gray-700 dark:text-gray-300 border-l border-gray-300/50 dark:border-gray-600/50 pl-2 flex items-center justify-center">
                  <Info size={18} className="mr-1 text-primary-600 dark:text-primary-400" />
                  <span className="text-lg">Weekly</span>
                </div>
                
                {/* Render weeks with weekly stats */}
                {weeks.map((week, weekIndex) => (
                  <React.Fragment key={`week-${weekIndex}`}>
                    {/* Render days in the week */}
                    {week.map((day, dayIndex) => (
                      <DayCell 
                        key={`day-${weekIndex}-${dayIndex}`} 
                        day={day} 
                        mode="plan"
                      />
                    ))}
                    
                    {/* Render weekly stats */}
                    <WeeklyStats
                      stats={weeklyStats[weekIndex]}
                      mode="plan"
                      isMobile={isMobile}
                    />
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Legend for both mobile and desktop with different implementations */}
            {isMobile ? (
              <div className="mt-8 bg-white dark:bg-gray-800 flex flex-wrap items-center justify-end gap-3 text-sm text-gray-700 dark:text-gray-300 p-3 rounded-xl shadow-sm border border-gray-300 dark:border-gray-600">
                {false ? (
                  <>
                    <div className="flex items-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                        <circle cx="8" cy="8" r="8" fill="#34D399" />
                      </svg>
                      <span>Profitable</span>
                    </div>
                    <div className="flex items-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                        <circle cx="8" cy="8" r="8" fill="#FBBF24" />
                      </svg>
                      <span>Breakeven</span>
                    </div>
                    <div className="flex items-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                        <circle cx="8" cy="8" r="8" fill="#F87171" />
                      </svg>
                      <span>Loss</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                        <circle cx="8" cy="8" r="8" fill="#34D399" />
                      </svg>
                      <span>≥70% Plan</span>
                    </div>
                    <div className="flex items-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                        <circle cx="8" cy="8" r="8" fill="#FBBF24" />
                      </svg>
                      <span>50-69% Plan</span>
                    </div>
                    <div className="flex items-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                        <circle cx="8" cy="8" r="8" fill="#F87171" />
                      </svg>
                      <span>{'<50% Plan'}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center">
                  <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                    <circle cx="8" cy="8" r="7.5" fill="white" stroke="#9CA3AF" strokeWidth="1" />
                  </svg>
                  <span>No Trades</span>
                </div>
              </div>
            ) : (
              <div className="mt-8 flex flex-wrap items-center justify-end gap-3 text-sm text-gray-700 dark:text-gray-300 backdrop-blur-sm p-3 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-600/50 bg-white/50 dark:bg-gray-800/50">
                {false ? (
                  <>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-emerald-300 rounded-full mr-1.5 shadow-sm"></div>
                      <span>Profitable</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-amber-300 rounded-full mr-1.5 shadow-sm"></div>
                      <span>Breakeven</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-red-300 rounded-full mr-1.5 shadow-sm"></div>
                      <span>Loss</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-emerald-300 rounded-full mr-1.5 shadow-sm"></div>
                      <span>≥70% Plan</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-amber-300 rounded-full mr-1.5 shadow-sm"></div>
                      <span>50-69% Plan</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-red-300 rounded-full mr-1.5 shadow-sm"></div>
                      <span>&lt;50% Plan</span>
                    </div>
                  </>
                )}
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-white dark:bg-gray-700 border border-gray-300/50 dark:border-gray-500 rounded-full mr-1.5 shadow-sm"></div>
                  <span>No Trades</span>
                </div>
              </div>
            )}
            </div>
            
            {/* P&L Tab Panel */}
            <div
              id="calendar-panel-pnl"
              role="tabpanel"
              aria-labelledby="calendar-tab-pnl"
              hidden={activeTab !== 'pnl'}
            >
              {/* Mobile view */}
              {isMobile ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-7 gap-1">
                    {/* Day header cells */}
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (
                      <div key={day} className="text-center font-medium py-1 text-gray-700 dark:text-gray-300 text-xs">
                        {day}
                      </div>
                    ))}
                    
                    {/* Render weeks without weekly stats column */}
                    {weeks.map((week, weekIndex) => (
                      <React.Fragment key={`week-${weekIndex}`}>
                        {/* Render days in the week */}
                        {week.map((day, dayIndex) => (
                          <DayCell 
                            key={`day-${weekIndex}-${dayIndex}`} 
                            day={day} 
                            mode="pnl"
                            isMobile={true}
                          />
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                  
                  {/* Mobile weekly stats */}
                  <div className="space-y-3">
                    {weeklyStats.map((stats, weekIndex) => (
                      stats.totalTrades > 0 ? (
                        <div 
                          key={`mobile-stats-${weekIndex}`} 
                          className={`rounded-xl p-3 shadow-sm border border-gray-200/50 dark:border-gray-600/50 ${getMobileStatsBgClass(stats)}`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="font-medium text-sm text-gray-800 dark:text-gray-200">Week {weekIndex + 1}</div>
                            <div className="text-xs text-gray-700 dark:text-gray-300">{stats.totalTrades} trades</div>
                          </div>
                          <div className="flex justify-between mt-2">
                            <div className="flex items-center">
                              <span className="text-sm font-medium mr-2 text-gray-800 dark:text-gray-200">Win Rate:</span>
                              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{stats.winRate.toFixed(0)}%</span>
                              <span className="text-xs ml-1 text-gray-600 dark:text-gray-400">W/L: {stats.winningTrades}/{stats.losingTrades}</span>
                            </div>
                          </div>
                        </div>
                      ) : null
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-8 gap-3 min-h-[750px]" style={{ gridAutoRows: "1fr" }}>
                  {/* Day header cells */}
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="text-center font-medium py-3 text-gray-700 dark:text-gray-300 text-lg">
                      {day}
                    </div>
                  ))}
                  
                  {/* Weekly stats header */}
                  <div className="text-center font-medium py-3 text-gray-700 dark:text-gray-300 border-l border-gray-300/50 dark:border-gray-600/50 pl-2 flex items-center justify-center">
                    <Info size={18} className="mr-1 text-primary-600 dark:text-primary-400" />
                    <span className="text-lg">Weekly</span>
                  </div>
                  
                  {/* Render weeks with weekly stats */}
                  {weeks.map((week, weekIndex) => (
                    <React.Fragment key={`week-${weekIndex}`}>
                      {/* Render days in the week */}
                      {week.map((day, dayIndex) => (
                        <DayCell 
                          key={`day-${weekIndex}-${dayIndex}`} 
                          day={day} 
                          mode="pnl"
                        />
                      ))}
                      
                      {/* Render weekly stats */}
                      <WeeklyStats
                        stats={weeklyStats[weekIndex]}
                        mode="pnl"
                        isMobile={isMobile}
                      />
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Legend for both mobile and desktop with different implementations */}
              {isMobile ? (
                <div className="mt-8 bg-white dark:bg-gray-800 flex flex-wrap items-center justify-end gap-3 text-sm text-gray-700 dark:text-gray-300 p-3 rounded-xl shadow-sm border border-gray-300 dark:border-gray-600">
                  <div className="flex items-center">
                    <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                      <circle cx="8" cy="8" r="8" fill="#34D399" />
                    </svg>
                    <span>Profitable</span>
                  </div>
                  <div className="flex items-center">
                    <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                      <circle cx="8" cy="8" r="8" fill="#EF4444" />
                    </svg>
                    <span>Loss</span>
                  </div>
                  <div className="flex items-center">
                    <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                      <circle cx="8" cy="8" r="8" fill="#94A3B8" />
                    </svg>
                    <span>No Trades</span>
                  </div>
                </div>
              ) : (
                <div className="mt-8 bg-white dark:bg-gray-800 flex flex-wrap items-center justify-end gap-3 text-sm text-gray-700 dark:text-gray-300 p-3 rounded-xl shadow-sm border border-gray-300 dark:border-gray-600">
                  <div className="flex items-center">
                    <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                      <circle cx="8" cy="8" r="8" fill="#34D399" />
                    </svg>
                    <span>Profitable</span>
                  </div>
                  <div className="flex items-center">
                    <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                      <circle cx="8" cy="8" r="8" fill="#EF4444" />
                    </svg>
                    <span>Loss</span>
                  </div>
                  <div className="flex items-center">
                    <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1.5">
                      <circle cx="8" cy="8" r="8" fill="#94A3B8" />
                    </svg>
                    <span>No Trades</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* NEW: Foldable X/Twitter share prompt - responsive positioning */}
        <div className="md:fixed md:bottom-6 md:right-6 md:max-w-xs md:z-50 mt-8 mx-auto max-w-md md:mx-0">
          <div 
            className={`glass-card border border-gray-300/50 dark:border-gray-600/50 bg-white/80 dark:bg-gray-800/80 backdrop-blur-[100px] shadow-lg rounded-2xl transition-all duration-300 cursor-pointer ${
              isSharePromptExpanded ? 'p-4' : 'p-2 hover:p-3'
            }`}
            onClick={isSharePromptExpanded ? undefined : toggleSharePrompt}
          >
            <div className="flex items-center gap-3">
              {/* X logo */}
              <div className="bg-black dark:bg-white rounded-full p-2 flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.1761 4H20.3037L14.0516 11.0952L21.5 20H15.0224L10.643 14.6607L5.63733 20H2.5L9.14842 12.547L2 4H8.64103L12.6145 8.89249L17.1761 4ZM15.8398 18.2H17.4668L7.77933 5.72H6.05438L15.8398 18.2Z" className="fill-white dark:fill-black"/>
                </svg>
              </div>
              
              {/* Message text - only show when expanded */}
              {isSharePromptExpanded && (
                <>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 flex-1">
                    Proudly sticking to your trading plan? Share screenshots and tag us on X to motivate others! <span className="text-blue-500 dark:text-blue-400">@SimplifiedTJ</span>
                  </p>
                  
                  {/* Minimize button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering the expand click
                      toggleSharePrompt();
                    }}
                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                    aria-label="Minimize share prompt"
                  >
                    <Minus size={14} className="text-gray-500 dark:text-gray-400" />
                  </button>
                </>
              )}
            </div>
            
            {/* Collapsed state hint */}
            {!isSharePromptExpanded && (
              <div className="mt-1 text-center">
                <div className="w-8 h-0.5 bg-gray-400 dark:bg-gray-500 rounded-full mx-auto opacity-60"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};